'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef } from 'react';
import { useIsDarkTheme } from '@/hooks/useIsDarkTheme';
import { useMediaQuery } from '@/hooks/useIsMobile';
import { useMountEffect } from '@/hooks/useMountEffect';
import type { FigSource } from './Fig';

// Same as Fig's PHONE_QUERY. Fig imports this file, so the constant can't come
// from there without a cycle.
const PHONE_QUERY = '(max-width: 600px)';

// The same fallback order as the essays' reader: phone and dark, then phone,
// then dark, then the default.
function pick(phone: boolean, dark: boolean, twins: {
  base: FigSource;
  dark?: FigSource;
  m?: FigSource;
  mDark?: FigSource;
}) {
  return phone && dark && twins.mDark || phone && twins.m || dark && twins.dark || twins.base;
}

// A figure's recording: muted, looping and inline, playing while on screen.
// The server renders the light desktop file; the theme and viewport are read
// after hydration (both snapshots start false), so the markup always matches
// and the right twin swaps in on the next commit.
export function StoryVideo(t0) {
  const $ = _c(15);
  const {
    base,
    dark,
    m,
    mDark,
    alt
  } = t0;
  const ref = useRef(null);
  const onScreen = useRef(false);
  const phone = useMediaQuery(PHONE_QUERY);
  const isDark = useIsDarkTheme();
  let t1;
  if ($[0] !== base || $[1] !== dark || $[2] !== isDark || $[3] !== m || $[4] !== mDark || $[5] !== phone) {
    const twins = {
      base,
      dark,
      m,
      mDark
    };
    t1 = pick(phone, isDark, twins);
    $[0] = base;
    $[1] = dark;
    $[2] = isDark;
    $[3] = m;
    $[4] = mDark;
    $[5] = phone;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const file = t1;
  const poster = phone && isDark && mDark?.poster || phone && m?.poster || isDark && dark?.poster || base.poster;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const video = ref.current;
      if (!video) {
        return;
      }
      video.muted = true;
      const observer = new IntersectionObserver(t3 => {
        const [entry] = t3;
        onScreen.current = !!entry?.isIntersecting;
        if (onScreen.current) {
          video.play().catch(_temp);
        } else {
          video.pause();
        }
      }, {
        rootMargin: "200px 0px"
      });
      observer.observe(video);
      return () => observer.disconnect();
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useMountEffect(t2);
  const t3 = alt || undefined;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      if (onScreen.current) {
        e.currentTarget.play().catch(_temp2);
      }
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== file.h || $[10] !== file.src || $[11] !== file.w || $[12] !== poster || $[13] !== t3) {
    t5 = <video ref={ref} src={file.src} poster={poster} width={file.w} height={file.h} aria-label={t3} muted={true} loop={true} playsInline={true} preload="metadata" onLoadedData={t4} />;
    $[9] = file.h;
    $[10] = file.src;
    $[11] = file.w;
    $[12] = poster;
    $[13] = t3;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp2() {}
function _temp() {}